import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  buildLocalizedPath,
  isLocalizedRoute,
  localeFromPath,
  stripLocalePrefix,
  type Locale,
} from '@/i18n';
import { salvarIdioma } from '@/i18n/preferencia';

/**
 * Retorna helpers para trabalhar com URLs multi-locale.
 * - `localizedTo(path)` → adiciona o prefixo do idioma corrente
 * - `switchLocale(locale)` → navega para a MESMA página no novo idioma
 * - `currentLocale` → idioma detectado pelo path atual
 */
export function useLocalizedNavigate() {
  const location = useLocation();
  const navigate = useNavigate();
  const { i18n } = useTranslation();

  const currentLocale: Locale = localeFromPath(location.pathname);

  const localizedTo = useCallback(
    (logicalPath: string): string => buildLocalizedPath(logicalPath, currentLocale),
    [currentLocale],
  );

  const switchLocale = useCallback(
    (target: Locale) => {
      const logical = stripLocalePrefix(location.pathname);
      i18n.changeLanguage(target);
      salvarIdioma(target);

      // /app/* e /login existem numa única versão, sem prefixo. Navegar para
      // /en/app/... não casa com rota nenhuma e a tela fica em branco — nesses
      // caminhos o idioma troca em memória e a URL permanece.
      if (!isLocalizedRoute(logical)) return;

      navigate(buildLocalizedPath(logical, target) + location.search, { replace: false });
    },
    [i18n, location.pathname, location.search, navigate],
  );

  return { currentLocale, localizedTo, switchLocale };
}
